import { Navigate } from "react-router-dom";
import { inject } from "mobx-react";
import AuthStore from "../../store/auth-store";
import { FeatureStore, TransitionStore } from "../../store";
import { ElementType, ReactNode } from "react";
import { Features, ROLE_DEFAULT_PATH, Roles, ROUTES } from "./utils";

interface PrivateRouteProps {
  authStore?: AuthStore;
  featureStore?: FeatureStore;
  transitionStore?: TransitionStore;
  component: ElementType;
  children?: ReactNode;
  requiredRoles?: Roles[];
  requiredFeatures?: Features[];
  shouldRedirect?: boolean;
}

function AuthorizedRoute(props: PrivateRouteProps) {
  const {
    authStore,
    featureStore,
    component: Component,
    shouldRedirect,
    requiredRoles,
    requiredFeatures,
  } = props;

  const render = () => {
    return routeRenderer();
  };

  const routeRenderer = () => {
    const { isAuthenticated } = authStore!;

    if (!isAuthenticated) {
      return <Navigate to={{ pathname: ROUTES.LOGIN }} />;
    }

    if (
      !featureStore!.featureList.initialConfigCompleted &&
      authStore?.isAdmin() &&
      window.location.pathname !== ROUTES.INITIAL_CONFIGURATION
    ) {
      return <Navigate to={{ pathname: ROUTES.INITIAL_CONFIGURATION }} />;
    }

    if (isRoleAllowed() && isFeatureEnabled() && !shouldRedirect) {
      return <Component {...props} />;
    } else {
      return redirectBasedOnRole();
    }
  };

  const redirectBasedOnRole = () => {
    const role = getUserRole();

    if (role === Roles.UNKNOWN) return null;

    const roleToString = Roles[role];
    const path = ROLE_DEFAULT_PATH[roleToString];

    return <Navigate to={{ pathname: path }} />;
  };

  const getUserRole = (): number => {
    if (authStore?.isAdmin()) {
      return Roles.ADMIN;
    }
    if (authStore?.isNurseAdmin()) {
      return Roles.NURSEADMIN;
    }
    if (authStore?.isNurse()) {
      return Roles.NURSE;
    }
    return Roles.UNKNOWN;
  };

  const isFeatureEnabled = (): boolean => {
    if (!requiredFeatures) return true;
    let missingFeature = false;

    requiredFeatures.forEach((feature) => {
      if (featureStore!.featureList[feature] !== true) missingFeature = true;
    });

    if (missingFeature) return false;

    return true;
  };

  const isRoleAllowed = (): boolean => {
    const role = getUserRole();

    if (!requiredRoles) return true;

    if (requiredRoles.includes(role)) return true;
    return false;
  };

  return render();
}

export default inject(
  "authStore",
  "featureStore",
  "transitionStore"
)(AuthorizedRoute);
